(() => {
  'use strict';
  const ROOT = '[data-fitment-global]';
  const VERSION = 'fitment-global-v1';
  const STATIC_LOAD_MULTIPLIER = 3.5;
  const ROOF_LABELS = {
    fixed_points:'Vaste bevestigingspunten',
    integrated_rails:'Geïntegreerde dakrails',
    raised_rails:'Open dakrails',
    smooth_roof:'Glad dak'
  };
  const ROOF_KEYS = Object.keys(ROOF_LABELS);
  const cache = new Map();
  const trackingUrlPath = value => {
    if (!value) return '';
    try {const url=new URL(value,location.origin);return url.origin+url.pathname}
    catch (_) {return ''}
  };
  const trackingPath = value => {
    if (!value) return '';
    try {return new URL(value,location.origin).pathname}
    catch (_) {return ''}
  };
  const storage = (store,key,create) => {
    try {let value=store.getItem(key);if(!value){value=create();store.setItem(key,value)}return value}
    catch (_) {return create()}
  };
  const uid = () => (globalThis.crypto?.randomUUID?.() || `fg-${Date.now()}-${Math.random().toString(36).slice(2)}`);
  const hash = text => {
    let h=2166136261;
    for(let i=0;i<text.length;i++){h^=text.charCodeAt(i);h=Math.imul(h,16777619)}
    return h>>>0;
  };
  async function completionHash(text){
    if(globalThis.crypto?.subtle){
      const bytes=await crypto.subtle.digest('SHA-256',new TextEncoder().encode(text));
      return Array.from(new Uint8Array(bytes)).map(n=>n.toString(16).padStart(2,'0')).join('');
    }
    return hash(text).toString(16)+'-'+text.length;
  }
  const option = (value,label) => {const el=document.createElement('option');el.value=value;el.textContent=label;return el};
  const distinct = array => [...new Set(array)].sort((a,b)=>a.localeCompare(b,'nl'));
  const clear = (select,label) => {select.replaceChildren(option('',label));select.value='';select.disabled=true};
  const list = (select,items,label) => {select.replaceChildren(option('',label),...items.map(item=>option(item.value,item.label)));select.disabled=false};
  const wait = ms => new Promise(resolve=>setTimeout(resolve,ms));
  class FitmentGlobal {
    constructor(root){
      this.root=root;
      this.indexUrl=root.dataset.indexUrl;
      this.trackingUrl=root.dataset.trackingUrl;
      this.legacyUrls={options:root.dataset.legacyOptionsUrl,results:root.dataset.legacyResultsUrl,product:root.dataset.productJsonUrl};
      this.selects=Object.fromEntries(['make','model','variant','year','roof_type'].map(level=>[level,root.querySelector(`[data-level="${level}"]`)]));
      this.fields=Object.fromEntries(['variant','roof_type'].map(level=>[level,root.querySelector(`[data-field="${level}"]`)]));
      this.roofGuide=root.querySelector('[data-roof-guide]');
      this.status=root.querySelector('[data-status]');
      this.result=root.querySelector('[data-result]');
      this.resultShell=root.querySelector('[data-result-shell]');
      this.loader=root.querySelector('[data-loader]');
      this.contact=root.querySelector('[data-contact-panel]');
      this.contactToggle=root.querySelector('[data-not-found-toggle]');
      this.delivery=root.querySelector('[data-delivery]');
      this.deliveryBackdrop=root.querySelector('[data-delivery-backdrop]');
      this.deliveryStatus=root.querySelector('[data-delivery-status]');
      this.deliveryForm=this.delivery.querySelector('form');
      this.deliveryContent=this.delivery.querySelector('[data-delivery-content]');
      this.deliverySuccessView=this.delivery.querySelector('[data-delivery-success-view]');
      this.deliveryState='idle';this.deliveryTimer=0;this.deliveryPoll=0;this.successTimer=0;
      this.contactOpenedForUnknownRoof=false;
      this.loadedAt=performance.now();
      this.visitorId=storage(localStorage,'fitmentGlobalVisitorId',uid);
      this.sessionId=storage(sessionStorage,'fitmentGlobalSessionId',uid);
      const params=new URLSearchParams(location.search);
      const touch=()=>({first_utm_source:params.get('utm_source')||'',first_utm_medium:params.get('utm_medium')||'',
        first_utm_campaign:params.get('utm_campaign')||'',first_referrer:trackingUrlPath(document.referrer),
        landing_page:location.pathname});
      try {this.firstTouch=JSON.parse(storage(localStorage,'fitmentGlobalFirstTouch',()=>JSON.stringify(touch())))}
      catch (_) {this.firstTouch=touch()}
      this.state={make:'',model:'',variant:'',variantChosen:false,year:'',roof_type:''};
      this.rows=[];this.index=null;this.currentRow=null;this.lookupId='';this.renderToken=0;
      this.loaderTimers=[];this.dotTimer=null;
      this.autoScrollTimer=0;this.autoScrollActive=false;this.scrollIntentCleanup=null;this.lookupIntentCleanup=null;
      this.scrollInterruptedLookupId='';this.resultScrollLookupId='';
      this.returnFocus=null;
    }
    base(extra={}){
      const params=new URLSearchParams(location.search);
      const width=window.innerWidth;
      return {
        event_id:uid(),timestamp:new Date().toISOString(),selector_version:VERSION,
        dataset_version:this.index?.dataset_version||'loading',visitor_id:this.visitorId,
        session_id:this.sessionId,lookup_id:this.lookupId,event_type:'',selected_stage:'',
        make:this.state.make,model:this.state.model,variant:this.state.variant,year:this.state.year,
        roof_type:this.state.roof_type,roof_type_status:this.currentRow?.[5]||'',
        roof_type_source:this.currentRow?.[6]||'',result_found:false,displayed_roofbar_family:'',
        page_url:trackingUrlPath(location.href),page_path:location.pathname,
        device_type:width<600?'mobile':width<1100?'tablet':'desktop',
        referrer:trackingUrlPath(document.referrer),utm_source:params.get('utm_source')||'',
        utm_medium:params.get('utm_medium')||'',utm_campaign:params.get('utm_campaign')||'',
        ...this.firstTouch,first_referrer:trackingUrlPath(this.firstTouch.first_referrer),
        landing_page:trackingPath(this.firstTouch.landing_page),available_option_count:'',
        elapsed_seconds_since_load:Math.round((performance.now()-this.loadedAt)/1000),
        user_agent:navigator.userAgent,extra_data_json:'',...extra
      };
    }
    track(eventType,extra={},completion=null){
      if(!this.trackingUrl)return;
      const body=JSON.stringify({event:this.base({event_type:eventType,...extra}),completion});
      try {
        if(navigator.sendBeacon && navigator.sendBeacon(this.trackingUrl,new Blob([body],{type:'text/plain'})))return;
        fetch(this.trackingUrl,{method:'POST',mode:'no-cors',keepalive:true,headers:{'Content-Type':'text/plain'},body})
          .catch(error=>console.warn('Fitment Global tracking unavailable',error));
      } catch(error) {console.warn('Fitment Global tracking unavailable',error)}
    }
    setStatus(message,error=false){this.status.textContent=message;this.status.toggleAttribute('data-error',error)}
    clearLoaderMotion(){
      this.loaderTimers.forEach(clearTimeout);this.loaderTimers=[];
      if(this.dotTimer){clearInterval(this.dotTimer);this.dotTimer=null}
    }
    setLoaderStage(stage){
      this.loader?.querySelectorAll('[data-loader-stages] span').forEach((line,index)=>{
        line.classList.toggle('is-active',index===stage);
        line.classList.toggle('is-complete',index<stage);
      });
    }
    hideResult(){
      this.cancelAutoScroll();
      this.lookupIntentCleanup?.();this.lookupIntentCleanup=null;
      this.renderToken++;
      this.clearLoaderMotion();
      if(this.loader)this.loader.hidden=true;
      if(this.resultShell){this.resultShell.removeAttribute('data-mode');this.resultShell.setAttribute('aria-busy','false')}
      this.result.hidden=true;this.result.replaceChildren();this.result.removeAttribute('aria-hidden');this.result.inert=false;
      this.closeDelivery(false);
      this.currentRow=null;this.lookupId='';
    }
    cancelAutoScroll(){
      clearTimeout(this.autoScrollTimer);this.autoScrollTimer=0;
      if(this.autoScrollActive)window.scrollTo({top:window.scrollY,behavior:'instant'});
      this.autoScrollActive=false;
      this.scrollIntentCleanup?.();this.scrollIntentCleanup=null;
    }
    watchLookupScrollIntent(){
      this.lookupIntentCleanup?.();
      const lookupId=this.lookupId;
      const interrupt=event=>{
        if(event.type==='keydown'&&!['ArrowUp','ArrowDown','PageUp','PageDown','Home','End',' '].includes(event.key))return;
        this.scrollInterruptedLookupId=lookupId;
        this.cancelAutoScroll();
      };
      for(const name of ['wheel','touchstart','keydown'])window.addEventListener(name,interrupt,{passive:true});
      this.lookupIntentCleanup=()=>{
        for(const name of ['wheel','touchstart','keydown'])window.removeEventListener(name,interrupt);
      };
    }
    autoScroll(target){
      this.cancelAutoScroll();
      if(Math.abs(target-window.scrollY)<2)return;
      if(window.matchMedia('(prefers-reduced-motion: reduce)').matches){window.scrollTo({top:target,behavior:'instant'});return}
      const lookupId=this.lookupId;
      const interrupt=event=>{
        if(event.type==='keydown'&&!['ArrowUp','ArrowDown','PageUp','PageDown','Home','End',' '].includes(event.key))return;
        this.scrollInterruptedLookupId=lookupId;
        this.cancelAutoScroll();
      };
      for(const name of ['wheel','touchstart','keydown'])window.addEventListener(name,interrupt,{passive:true});
      this.scrollIntentCleanup=()=>{
        for(const name of ['wheel','touchstart','keydown'])window.removeEventListener(name,interrupt);
      };
      this.autoScrollActive=true;
      window.scrollTo({top:target,behavior:'smooth'});
      this.autoScrollTimer=setTimeout(()=>{
        this.autoScrollActive=false;
        this.scrollIntentCleanup?.();this.scrollIntentCleanup=null;
      },1100);
    }
    scrollToResult(){
      const header=document.querySelector('header-component, .section-header, .shopify-section-group-header-group');
      const headerHeight=header?.getBoundingClientRect().height||0;
      const focalY=Math.max(innerHeight*.45,headerHeight+24);
      const target=Math.max(0,this.resultShell.getBoundingClientRect().top+window.scrollY-focalY);
      this.autoScroll(target);
    }
    async scrollToCards(token,lookupId,cards){
      if(this.resultScrollLookupId===lookupId||this.scrollInterruptedLookupId===lookupId)return;
      // Card media has a fixed CSS height, so image decoding cannot move the card anchor.
      await new Promise(resolve=>requestAnimationFrame(()=>requestAnimationFrame(resolve)));
      if(token!==this.renderToken||lookupId!==this.lookupId||this.scrollInterruptedLookupId===lookupId)return;
      const header=document.querySelector('header-component, .section-header, .shopify-section-group-header-group');
      const headerHeight=header?.getBoundingClientRect().height||0;
      const focalY=Math.max(innerHeight*(innerWidth<600?.32:innerWidth<1100?.33:.35),headerHeight+24);
      const target=Math.max(0,cards.getBoundingClientRect().top+window.scrollY-focalY);
      this.resultScrollLookupId=lookupId;
      this.lookupIntentCleanup?.();this.lookupIntentCleanup=null;
      this.autoScroll(target);
    }
    startLoader(){
      if(!this.loader||!this.resultShell)return false;
      this.resultShell.dataset.mode='loading';this.resultShell.setAttribute('aria-busy','true');
      this.loader.hidden=false;
      const dots=this.loader.querySelector('[data-loader-dots]');
      let dotCount=3;
      if(dots){
        dots.textContent='BEZIG...';
        this.dotTimer=setInterval(()=>{dotCount=(dotCount+1)%4;dots.textContent=`BEZIG${'.'.repeat(dotCount)}`},250);
      }
      this.setLoaderStage(0);
      for(const [stage,delay] of [[1,700],[2,1100],[3,1500]]){
        this.loaderTimers.push(setTimeout(()=>this.setLoaderStage(stage),delay));
      }
      this.setStatus('Voertuiggegevens worden gecontroleerd.');
      return true;
    }
    async finishLoader(token){
      this.result.removeAttribute('aria-hidden');this.result.inert=false;
      this.resultShell.dataset.mode='resolving';this.resultShell.setAttribute('aria-busy','false');
      this.setStatus('');
      this.clearLoaderMotion();
      await wait(180);
      if(token!==this.renderToken)return false;
      this.loader.hidden=true;this.resultShell.dataset.mode='done';
      return true;
    }
    resetFrom(level){
      if(this.contactOpenedForUnknownRoof){
        this.contact.hidden=true;
        this.contactToggle.setAttribute('aria-expanded','false');
        this.contactOpenedForUnknownRoof=false;
      }
      const order=['make','model','variant','year','roof_type'];
      const at=order.indexOf(level);
      for(const later of order.slice(at+1)){
        this.state[later]='';
        clear(this.selects[later],later==='model'?'Kies eerst een merk':'Kies eerst de vorige stap');
      }
      this.state.variantChosen=false;
      if(at<2)this.fields.variant.hidden=true;
      if(at<4)this.fields.roof_type.hidden=true;
      if(at<4)this.roofGuide.open=false;
      this.hideResult();
    }
    async fetchJson(url){
      if(!cache.has(url))cache.set(url,fetch(url,{credentials:'same-origin'}).then(r=>{if(!r.ok)throw Error(`HTTP ${r.status}`);return r.json()}));
      try{return await cache.get(url)}catch(error){cache.delete(url);throw error}
    }
    async init(){
      const started=performance.now();
      for(const [level,select] of Object.entries(this.selects))select.addEventListener('change',()=>this.changed(level));
      this.contactToggle.addEventListener('click',()=>{this.contactOpenedForUnknownRoof=false;this.toggleContact()});
      this.contact.querySelector('form')?.addEventListener('submit',()=>this.contactSubmitted());
      this.deliveryForm.addEventListener('submit',event=>this.submitDelivery(event));
      this.delivery.querySelector('[data-delivery-close]').addEventListener('click',()=>this.closeDelivery());
      this.deliveryBackdrop.addEventListener('click',event=>{if(event.target===this.deliveryBackdrop)this.closeDelivery()});
      this.deliveryBackdrop.addEventListener('keydown',event=>this.deliveryKeydown(event));
      this.restoreDelivery();
      this.restoreContact();
      try {
        this.index=await this.fetchJson(this.indexUrl);
        if(!this.index.dataset_version||!this.index.makes)throw Error('Ongeldige voertuigindex');
        const makes=distinct(Object.keys(this.index.makes));
        list(this.selects.make,makes.map(x=>({value:x,label:x})),'Kies een merk');
        this.root.dataset.indexReadyMs=(performance.now()-started).toFixed(1);
        this.setStatus('Kies het merk van je auto.');
        this.track('selector_loaded',{selected_stage:'make',available_option_count:makes.length});
      } catch(error){this.setStatus('Voertuiggegevens konden niet worden geladen. Probeer het later opnieuw.',true);console.error(error)}
    }
    async changed(level){
      if(!this.deliveryStatus.hidden){this.deliveryStatus.hidden=true;clearTimeout(this.successTimer)}
      const started=performance.now();
      if(level==='make'){
        this.state.make=this.selects.make.value;this.resetFrom('make');
        if(!this.state.make)return;
        this.track('make_selected',{selected_stage:'make',available_option_count:Object.keys(this.index.makes).length});
        this.setStatus('Modellen laden…');
        this.selects.make.disabled=true;
        try {
          const shard=new URL(this.index.makes[this.state.make],new URL(this.indexUrl,location.href)).href;
          const data=await this.fetchJson(shard);
          if(data.dataset_version!==this.index.dataset_version||!data.makes[this.state.make])throw Error('Datasetversie verschilt');
          this.rows=data.makes[this.state.make];
          const models=distinct(this.rows.map(row=>row[0]));
          list(this.selects.model,models.map(x=>({value:x,label:x})),'Kies een model');
          this.root.dataset.shardReadyMs=(performance.now()-started).toFixed(1);
          this.setStatus('Kies het model.');
          if(!models.length)this.track('dropdown_no_options',{selected_stage:'model'});
        } catch(error){this.setStatus('Modellen konden niet worden geladen. Probeer opnieuw.',true);console.error(error)}
        finally{this.selects.make.disabled=false}
      } else if(level==='model'){
        this.state.model=this.selects.model.value;this.resetFrom('model');
        if(!this.state.model)return;
        const models=distinct(this.rows.map(row=>row[0]));
        this.track('model_selected',{selected_stage:'model',available_option_count:models.length});
        const variants=distinct(this.rows.filter(row=>row[0]===this.state.model).map(row=>row[1]));
        if(!variants.length){this.track('dropdown_no_options',{selected_stage:'variant'});return}
        list(this.selects.variant,variants.map(x=>({value:x||'__unspecified__',label:x||'Niet gespecificeerd'})),'Kies een uitvoering');
        if(variants.length===1){this.selects.variant.value=variants[0]||'__unspecified__';this.fields.variant.hidden=true;this.changed('variant',true)}
        else {this.fields.variant.hidden=false;this.setStatus('Kies de uitvoering die bij het dak van je auto hoort.')}
        this.root.dataset.modelReadyMs=(performance.now()-started).toFixed(1);
      } else if(level==='variant'){
        const value=this.selects.variant.value;
        this.state.variant=value==='__unspecified__'?'':value;
        this.state.variantChosen=Boolean(value);
        this.resetFrom('variant');this.state.variantChosen=Boolean(value);
        if(!value)return;
        const years=[...new Set(this.filtered().map(row=>row[2]))].sort((a,b)=>b-a);
        this.track('variant_selected',{selected_stage:'variant',available_option_count:years.length,extra_data_json:JSON.stringify({automatic:this.fields.variant.hidden})});
        list(this.selects.year,years.map(x=>({value:String(x),label:String(x)})),'Kies een bouwjaar');
        this.root.dataset.variantReadyMs=(performance.now()-started).toFixed(1);
        this.setStatus('Kies het bouwjaar.');
        if(!years.length)this.track('dropdown_no_options',{selected_stage:'year'});
      } else if(level==='year'){
        this.state.year=this.selects.year.value;this.resetFrom('year');
        if(!this.state.year)return;
        this.currentRow=this.filtered().find(row=>String(row[2])===this.state.year)||null;
        if(!this.currentRow){this.track('dropdown_no_options',{selected_stage:'roof_type'});this.setStatus('Deze combinatie ontbreekt. Gebruik het contactformulier hieronder.',true);return}
        this.track('year_selected',{selected_stage:'year',available_option_count:this.selects.year.options.length-1});
        const choices=ROOF_KEYS.filter((_,i)=>this.currentRow[4]&(1<<i));
        this.root.dataset.yearReadyMs=(performance.now()-started).toFixed(1);
        if(choices.length===1 && this.currentRow[5]==='verified'){
          this.state.roof_type=choices[0];this.fields.roof_type.hidden=true;
          this.track('roof_type_selected',{selected_stage:'roof_type',available_option_count:1,extra_data_json:JSON.stringify({automatic:true})});
          this.complete();
        } else {
          list(this.selects.roof_type,[...choices.map(x=>({value:x,label:ROOF_LABELS[x]})),{value:'__unknown__',label:'Ik weet het niet'}],'Kies je daktype');
          this.fields.roof_type.hidden=false;
          this.setStatus('Kies het daktype.');
          if(!choices.length)this.track('dropdown_no_options',{selected_stage:'roof_type'});
        }
      } else if(level==='roof_type'){
        const selectedRoof=this.selects.roof_type.value;
        this.state.roof_type=selectedRoof==='__unknown__'?'':selectedRoof;
        this.hideResult();
        this.currentRow=this.filtered().find(row=>String(row[2])===this.state.year)||null;
        if(selectedRoof==='__unknown__'){
          this.setStatus('Geen probleem. Beschrijf je dak in het contactformulier; we helpen je verder.');
          if(this.contact.hidden){this.toggleContact();this.contactOpenedForUnknownRoof=true}
          return;
        }
        if(this.contactOpenedForUnknownRoof){
          this.contact.hidden=true;
          this.contactToggle.setAttribute('aria-expanded','false');
          this.contactOpenedForUnknownRoof=false;
        }
        if(!this.state.roof_type||!this.currentRow)return;
        this.track('roof_type_selected',{selected_stage:'roof_type',available_option_count:this.selects.roof_type.options.length-1});
        this.complete();
      }
    }
    filtered(){return this.rows.filter(row=>row[0]===this.state.model && row[1]===this.state.variant)}
    async complete(){
      const started=performance.now();
      const token=++this.renderToken;
      const row=this.currentRow;
      if(!row||!this.state.roof_type)return;
      this.lookupId=uid();
      this.scrollInterruptedLookupId='';this.resultScrollLookupId='';
      this.watchLookupScrollIntent();
      this.track('lookup_started',{selected_stage:'result'});
      const selectionKey=[row[3],this.state.roof_type].join('|');
      const hasLoader=this.startLoader();
      if(hasLoader)this.scrollToResult();
      const productPromise=(window.FitmentGlobalLegacy?.resolve(this.state,this.legacyUrls) ||
        Promise.reject(new Error('Legacy resolver unavailable'))).catch(error=>{
          console.error('Fitment Global product resolution failed',error);return null;
        });
      let completionId;
      try {completionId=await completionHash([this.lookupId,selectionKey].join('|'))}
      catch(error){
        if(token===this.renderToken){
          this.hideResult();
          if(this.fields.roof_type.hidden){
            this.selects.year.value='';this.state.year='';this.state.roof_type='';
            this.setStatus('Het resultaat kon niet worden geladen. Kies het bouwjaar opnieuw.',true);
          } else {
            this.selects.roof_type.value='';this.state.roof_type='';
            this.setStatus('Het resultaat kon niet worden geladen. Kies je daktype opnieuw.',true);
          }
        }
        console.error(error);return;
      }
      if(token!==this.renderToken)return;
      const product=await productPromise;
      if(token!==this.renderToken)return;
      const family=product?.family||'';
      const resultMetadata=product?JSON.stringify({
        result_source:product.source,legacy_result_key:product.legacyKey,
        mapped_shopify_variant_id:String(product.variantId)
      }):'';
      const source=row[5]==='customer_required'?'customer_selection':row[6];
      const completed={
        completed_at:new Date().toISOString(),completion_id:completionId,
        visitor_id:this.visitorId,session_id:this.sessionId,lookup_id:this.lookupId,
        vehicle_key:row[3],make:this.state.make,model:this.state.model,variant:this.state.variant,
        year:this.state.year,roof_type:this.state.roof_type,roof_type_status:row[5],
        roof_type_source:source,roof_type_confidence:row[7],displayed_roofbar_family:family,
        page_path:location.pathname,device_type:this.base().device_type,
        utm_source:this.base().utm_source,utm_medium:this.base().utm_medium,
        utm_campaign:this.base().utm_campaign,...this.firstTouch,
        selector_version:VERSION,dataset_version:this.index.dataset_version
      };
      this.track('lookup_completed',{selected_stage:'result',roof_type_source:source,result_found:Boolean(product),displayed_roofbar_family:family,extra_data_json:resultMetadata},completed);
      this.root.dataset.resultReadyMs=(performance.now()-started).toFixed(1);
      if(hasLoader){
        await wait(Math.max(0,1900-(performance.now()-started)));
        if(token!==this.renderToken)return;
        if(!await this.finishLoader(token))return;
      }
      if(product){
        void this.render(product,token);
        this.track('roofbar_result_rendered',{selected_stage:'result',result_found:true,displayed_roofbar_family:family,extra_data_json:resultMetadata});
        this.setStatus('');
      } else this.renderError();
    }
    renderError(){
      const message=document.createElement('p');message.className='fitment-global__result-error';
      message.textContent='Het product kon niet worden geladen. Probeer het opnieuw of neem contact met ons op.';
      this.result.replaceChildren(message);this.result.hidden=false;
      this.setStatus(message.textContent,true);
    }
    money(cents,currency='EUR'){
      return new Intl.NumberFormat('nl-NL',{style:'currency',currency}).format(cents/100);
    }
    detailList(items){
      const details=document.createElement('dl');details.className='fitment-global__details';
      for(const [label,value] of items){
        if(!value)continue;
        const row=document.createElement('div');
        const term=document.createElement('dt');term.textContent=label;
        const description=document.createElement('dd');description.textContent=value;
        row.append(term,description);details.append(row);
      }
      return details;
    }
    card(product,{roofbar=false}={}){
      const card=document.createElement('article');card.className='fitment-global__card';
      const media=document.createElement('div');media.className='fitment-global__media';
      const imageUrl=roofbar?product.image:product.imageUrl;
      if(imageUrl){
        const image=document.createElement('img');image.className='fitment-global__product-image';
        image.src=imageUrl;image.alt=roofbar?product.variantTitle:product.imageAlt;
        image.loading='lazy';image.decoding='async';media.append(image);
      }
      if(roofbar){
        const badge=document.createElement('span');badge.className='fitment-global__badge';badge.textContent='Levering op aanvraag';media.append(badge);
      }
      const info=document.createElement('div');info.className='fitment-global__product-info';
      const title=document.createElement('h4');title.textContent=roofbar?product.family:product.title;
      const price=document.createElement('p');price.className='fitment-global__price';price.textContent=this.money(product.priceCents,product.currency);
      const details=roofbar?this.detailList([
        ['Gekozen auto',[this.state.make,this.state.model,this.state.variant].filter(Boolean).join(' ')],
        ['MAX. STATISCHE BELASTING',product.dynamicLoad?`${product.dynamicLoad*STATIC_LOAD_MULTIPLIER} kg`:'Niet beschikbaar'],
        ['Daktype',ROOF_LABELS[this.state.roof_type]]
      ]):this.detailList([
        ['Gewicht',product.weightKg],['Openingstijd',this.openingTimeCopy(product.openingTime)],['Matras',product.mattressSize]
      ]);
      const action=roofbar?document.createElement('button'):document.createElement('a');
      action.className='fitment-global__cta';action.textContent=roofbar?'LEVERING AANVRAGEN':'BEKIJK DAKTENT';
      if(roofbar){
        action.type='button';action.addEventListener('click',()=>this.openDelivery(product));
      }else{
        action.href=product.url;
        action.addEventListener('click',()=>this.track('recommended_tent_click',{
          selected_stage:'result',result_found:true,
          extra_data_json:JSON.stringify({recommended_product_id:String(product.productId),recommendation_rule:this.recommendationRule})
        }));
      }
      info.append(title,price,details,action);card.append(media,info);return card;
    }
    openingTimeCopy(value){
      if(!value)return '';
      if(/automatisch/i.test(value))return 'Automatisch open in 10s';
      if(/handmatig|openen|opzetten/i.test(value))return 'Handmatig openen in 2 min';
      return value;
    }
    async render(product,token,autoScroll=true){
      const heading=document.createElement('h3');heading.className='fitment-global__result-heading';heading.textContent='Jouw set-up';
      const cards=document.createElement('div');cards.className='fitment-global__cards';
      cards.append(this.card(product,{roofbar:true}));
      this.result.replaceChildren(heading,this.installationStep(),cards);this.result.hidden=false;
      this.result.removeAttribute('aria-hidden');this.result.inert=false;
      await this.renderRecommendations(product,token,cards);
      if(autoScroll)await this.scrollToCards(token,this.lookupId,cards);
    }
    installationStep(){
      const install=document.createElement('section');install.className='fitment-global__next-step';
      const installHeading=document.createElement('h3');installHeading.textContent='Montage nodig?';
      const installCopy=document.createElement('p');installCopy.textContent='Laat je daktent en dakdragers monteren.';
      const installLink=document.createElement('a');installLink.href='/pages/installatie';installLink.textContent='BEKIJK MONTAGE';
      installLink.addEventListener('click',()=>this.track('installation_cta_click',{selected_stage:'result',result_found:true}));
      install.append(installHeading,installCopy,installLink);return install;
    }
    async renderRecommendations(roofbar,token,cards){
      const engine=window.FitmentGlobalTents;
      if(!engine){console.error('Tent recommendation module unavailable');return}
      const {rule,keys}=engine.choose(roofbar.dynamicLoad,roofbar.source);
      this.recommendationRule=rule;
      const products=await Promise.allSettled(keys.map(key=>engine.resolve(key)));
      if(token!==this.renderToken)return;
      const tents=products.filter(item=>item.status==='fulfilled').map(item=>item.value);
      tents.forEach(tent=>cards.append(this.card(tent)));
      if(tents.length!==2){
        const error=document.createElement('p');error.className='fitment-global__result-error';
        error.textContent='Niet alle daktenten konden worden geladen. Bekijk het assortiment in de winkel.';
        this.result.append(error);console.error('Tent recommendations incomplete',products.filter(item=>item.status==='rejected'));
      }
      if(tents.length===2)this.track('tent_recommendations_rendered',{
        selected_stage:'result',result_found:true,
        extra_data_json:JSON.stringify({recommended_product_ids:tents.map(item=>String(item.productId)),recommendation_rule:rule})
      });
    }
    openDelivery(product,trackClick=true){
      const extra_data_json=JSON.stringify({result_source:product.source,legacy_result_key:product.legacyKey,mapped_shopify_variant_id:String(product.variantId)});
      if(trackClick){
        this.track('roofbar_result_click',{selected_stage:'result',result_found:true,displayed_roofbar_family:product.family,extra_data_json});
        this.track('custom_delivery_click',{selected_stage:'result',result_found:true,displayed_roofbar_family:product.family,extra_data_json});
      }
      this.deliveryProduct=product;
      const values={lookup:this.lookupId,make:this.state.make,model:this.state.model,variant:this.state.variant,
        year:this.state.year,roof:ROOF_LABELS[this.state.roof_type],family:product.family,
        productId:String(product.productId),variantId:String(product.variantId),sku:product.sku,
        price:this.money(product.priceCents,product.currency),pagePath:location.pathname};
      for(const [key,value] of Object.entries(values))this.delivery.querySelector(`[data-request-${key.replace(/[A-Z]/g,char=>'-'+char.toLowerCase())}]`).value=value;
      const context=this.delivery.querySelector('[data-delivery-context]');
      const vehicle=[values.make,values.model,values.variant,values.year].filter(Boolean).join(' · ');
      context.replaceChildren(this.detailList([['Jouw auto',vehicle],['Daktype',values.roof],['Dakdragers',`${values.family} · ${values.price}`]]));
      this.stopDeliveryWatch();this.setDeliveryState('idle');
      if(trackClick)this.returnFocus=document.activeElement;
      this.deliveryBackdrop.hidden=false;
      this.delivery.hidden=false;
      if(!this.previousBodyOverflow)this.previousBodyOverflow=document.body.style.overflow;
      document.body.style.overflow='hidden';
      requestAnimationFrame(()=>this.delivery.querySelector('[data-delivery-close]').focus({preventScroll:true}));
    }
    closeDelivery(returnFocus=true){
      if(!this.deliveryBackdrop||this.deliveryBackdrop.hidden)return;
      this.stopDeliveryWatch();
      this.deliveryBackdrop.hidden=true;this.delivery.hidden=true;
      this.delivery.removeAttribute('data-state');this.delivery.removeAttribute('aria-label');
      this.delivery.style.width='';this.delivery.style.height='';this.delivery.style.maxWidth='';
      this.delivery.style.maxHeight='';this.delivery.style.padding='';this.delivery.style.overflow='';
      this.delivery.style.transform='';this.delivery.style.transition='';
      this.deliveryContent.inert=false;this.deliverySuccessView.hidden=true;
      this.setDeliveryState('idle');
      document.body.style.overflow=this.previousBodyOverflow??'';
      this.previousBodyOverflow=null;
      if(returnFocus&&this.returnFocus?.isConnected)this.returnFocus.focus({preventScroll:true});
      this.returnFocus=null;
    }
    setDeliveryState(state,message=''){
      if(!['idle','validating','captcha','submitting','success','error'].includes(state))throw Error('Invalid delivery state');
      this.deliveryState=state;this.delivery.dataset.requestState=state;
      const feedback=this.delivery.querySelector('[data-delivery-feedback]');
      const copy={captcha:'Voltooi de beveiligingscontrole om je aanvraag te versturen.',
        submitting:'Aanvraag wordt verstuurd…',error:'Aanvraag niet verstuurd. Probeer het opnieuw.'};
      feedback.textContent=message||copy[state]||'';
      feedback.hidden=!feedback.textContent;
      this.deliveryForm.querySelector('[data-delivery-submit]').disabled=state==='submitting';
      this.deliveryForm.setAttribute('aria-busy',String(state==='submitting'));
    }
    stopDeliveryWatch(){
      clearTimeout(this.deliveryTimer);clearInterval(this.deliveryPoll);
      this.deliveryTimer=0;this.deliveryPoll=0;
    }
    captchaChallengeVisible(){
      return [...document.querySelectorAll('iframe[title="hCaptcha challenge"]')].some(frame=>{
        const rect=frame.getBoundingClientRect();
        return rect.width>0&&rect.height>0&&rect.bottom>0&&rect.top<innerHeight&&
          getComputedStyle(frame).visibility==='visible'&&getComputedStyle(frame.parentElement).visibility==='visible';
      });
    }
    beginSubmitting(){
      this.stopDeliveryWatch();this.setDeliveryState('submitting');
      this.deliveryTimer=setTimeout(()=>{
        if(this.deliveryState==='submitting')this.setDeliveryState('error','Aanvraag niet bevestigd. Je gegevens staan er nog; probeer het opnieuw.');
      },25000);
    }
    watchCaptcha(){
      const token=this.deliveryForm.querySelector('[name="h-captcha-response"]');
      if(!token||token.value){this.beginSubmitting();return}
      this.setDeliveryState('captcha');
      this.deliveryPoll=setInterval(()=>{
        if(this.deliveryState!=='captcha'){this.stopDeliveryWatch();return}
        if(this.deliveryForm.querySelector('[name="h-captcha-response"]')?.value)this.beginSubmitting();
      },250);
      this.deliveryTimer=setTimeout(()=>{
        if(this.deliveryState!=='captcha')return;
        if(this.captchaChallengeVisible()){
          this.setDeliveryState('captcha','Rond de beveiligingscontrole af. Lukt dit niet, sluit haar en probeer opnieuw.');
        }else{
          this.stopDeliveryWatch();
          this.setDeliveryState('error','Beveiligingscontrole kon niet worden afgerond. Probeer het opnieuw.');
        }
      },25000);
    }
    deliveryKeydown(event){
      if(this.deliveryBackdrop.hidden)return;
      if(event.key==='Escape'){event.preventDefault();this.closeDelivery();return}
      if(event.key!=='Tab')return;
      const controls=[...this.delivery.querySelectorAll('a[href],button:not([disabled]),input:not([type="hidden"]):not([disabled]),textarea:not([disabled]),select:not([disabled])')].filter(item=>item.getClientRects().length);
      if(!controls.length){event.preventDefault();this.delivery.focus();return}
      const first=controls[0],last=controls.at(-1);
      if(event.shiftKey&&document.activeElement===first){event.preventDefault();last.focus()}
      else if(!event.shiftKey&&document.activeElement===last){event.preventDefault();first.focus()}
    }
    submitDelivery(event){
      const form=this.deliveryForm;
      if(this.deliveryState==='submitting'){event.preventDefault();return}
      this.stopDeliveryWatch();this.setDeliveryState('validating');
      const plate=form.querySelector('[name="contact[kenteken]"]');
      plate.value=plate.value.trim().toLocaleUpperCase('nl-NL');
      if(!form.reportValidity()){event.preventDefault();this.setDeliveryState('idle');return}
      try{
        sessionStorage.setItem(`fitmentGlobalDelivery-${this.root.id}`,JSON.stringify({
          savedAt:Date.now(),pagePath:location.pathname,state:this.state,lookupId:this.lookupId,
          product:this.deliveryProduct,datasetVersion:this.index?.dataset_version||'unknown',
          email:form.querySelector('[name="contact[email]"]').value,
          plate:plate.value,phone:form.querySelector('[name="contact[phone]"]').value,
          question:form.querySelector('[name="contact[body]"]').value
        }));
        sessionStorage.setItem(`fitmentGlobalActiveForm-${this.root.id}`,'delivery');
      }catch(error){console.warn('Could not preserve delivery form across Shopify response',error)}
      // Shopify owns the protected native POST. A submit event can be held by hCaptcha;
      // only a CAPTCHA token means it has advanced past that verification step.
      this.watchCaptcha();
    }
    showDeliverySuccess(saved){
      this.state=saved.state;this.lookupId=saved.lookupId;
      this.openDelivery(saved.product,false);
      this.deliverySuccessView.hidden=false;
      this.setDeliveryState('success');
      this.deliveryContent.inert=true;
      const rect=this.delivery.getBoundingClientRect();
      this.delivery.style.width='180px';
      this.delivery.style.height='180px';
      this.delivery.style.maxWidth='none';this.delivery.style.maxHeight='none';
      this.delivery.style.overflow='hidden';this.delivery.style.padding='0';
      this.delivery.style.transition='none';
      this.delivery.style.transform=`scale(${rect.width/180},${rect.height/180})`;
      this.delivery.getBoundingClientRect();
      requestAnimationFrame(()=>{
        this.delivery.style.transition='';
        this.delivery.dataset.state='success';
        this.delivery.style.transform='scale(1)';
        this.delivery.setAttribute('aria-label','Aanvraag verstuurd.');
        this.delivery.focus({preventScroll:true});
      });
      clearTimeout(this.successTimer);
      this.successTimer=setTimeout(()=>{
        this.closeDelivery(false);
        this.deliveryForm.reset();
        this.state={make:'',model:'',variant:'',variantChosen:false,year:'',roof_type:''};
        this.selects.make.value='';this.resetFrom('make');
        this.setStatus('Kies het merk van je auto.');
        this.deliveryStatus.hidden=false;
        const header=document.querySelector('header-component, .section-header, .shopify-section-group-header-group');
        const headerHeight=header?.getBoundingClientRect().height||0;
        window.scrollTo({top:Math.max(0,this.root.getBoundingClientRect().top+window.scrollY-headerHeight-12),behavior:'instant'});
        this.successTimer=setTimeout(()=>{this.deliveryStatus.hidden=true},5000);
      },1400);
    }
    restoreDelivery(){
      const key=`fitmentGlobalDelivery-${this.root.id}`;
      let saved;
      try{saved=JSON.parse(sessionStorage.getItem(key)||'null')}catch(_){return}
      if(!saved||saved.pagePath!==location.pathname||Date.now()-saved.savedAt>600000)return;
      if(sessionStorage.getItem(`fitmentGlobalActiveForm-${this.root.id}`)==='help')return;
      const params=new URLSearchParams(location.search);
      const errors=Boolean(this.delivery.querySelector('[data-delivery-errors]'));
      const success=!errors&&(Boolean(this.delivery.querySelector('[data-delivery-success]'))||params.get('contact_posted')==='true');
      if(success){
        params.delete('contact_posted');params.delete('fitment_delivery');
        history.replaceState(history.state,'',`${location.pathname}${params.size?`?${params}`:''}`);
        this.index={dataset_version:saved.datasetVersion};
        this.track('contact_form_submitted',{
          lookup_id:saved.lookupId,make:saved.state.make,model:saved.state.model,
          variant:saved.state.variant,year:saved.state.year,roof_type:saved.state.roof_type,
          displayed_roofbar_family:saved.product.family,result_found:true,
          selected_stage:'product_contact',extra_data_json:JSON.stringify({
          email_present:true,phone_present:Boolean(saved.phone),message_present:Boolean(saved.question)
        })});
        sessionStorage.removeItem(key);
        sessionStorage.removeItem(`fitmentGlobalActiveForm-${this.root.id}`);
        this.showDeliverySuccess(saved);
        return;
      }
      this.state=saved.state;this.lookupId=saved.lookupId;
      this.index={dataset_version:saved.datasetVersion};
      this.resultShell.dataset.mode='done';
      void this.render(saved.product,this.renderToken,false);
      this.openDelivery(saved.product,false);
      const form=this.deliveryForm;
      for(const [name,value] of Object.entries({email:saved.email,kenteken:saved.plate,phone:saved.phone,body:saved.question})){
        const field=form.querySelector(`[name="contact[${name}]"]`);if(field)field.value=value||'';
      }
      this.setDeliveryState('error','Aanvraag niet bevestigd. Je gegevens staan er nog; probeer het opnieuw.');
    }
    toggleContact(){
      const open=this.contact.hidden;
      this.contact.hidden=!open;this.contactToggle.setAttribute('aria-expanded',String(open));
      if(open){
        const vehicle=this.contact.querySelector('[data-contact-vehicle]');
        vehicle.value=[this.state.make,this.state.model,this.state.variant,this.state.year,this.state.roof_type?ROOF_LABELS[this.state.roof_type]:''].filter(Boolean).join(' · ');
        this.track('car_not_found_click',{selected_stage:this.state.year?'year':this.state.model?'model':this.state.make?'make':'start'});
        vehicle.focus();
      }
    }
    contactSubmitted(){
      const form=this.contact.querySelector('form');
      if(!form?.reportValidity())return;
      try{
        sessionStorage.setItem(`fitmentGlobalHelpContact-${this.root.id}`,JSON.stringify({
          savedAt:Date.now(),pagePath:location.pathname,lookupId:this.lookupId,
          state:this.state,datasetVersion:this.index?.dataset_version||'unknown',
          phonePresent:Boolean(form.querySelector('[name="contact[phone]"]')?.value),
          messagePresent:Boolean(form.querySelector('[name="contact[body]"]')?.value)
        }));
        sessionStorage.setItem(`fitmentGlobalActiveForm-${this.root.id}`,'help');
      }catch(error){console.warn('Could not preserve help-form tracking state',error)}
    }
    restoreContact(){
      const key=`fitmentGlobalHelpContact-${this.root.id}`;
      let saved;
      try{saved=JSON.parse(sessionStorage.getItem(key)||'null')}catch(_){return}
      if(!saved||saved.pagePath!==location.pathname||Date.now()-saved.savedAt>600000)return;
      if(sessionStorage.getItem(`fitmentGlobalActiveForm-${this.root.id}`)!=='help')return;
      const success=Boolean(this.contact.querySelector('[data-contact-success]'));
      const errors=Boolean(this.contact.querySelector('[data-contact-errors]'));
      if(!success&&!errors)return;
      this.contact.hidden=false;this.contactToggle.setAttribute('aria-expanded','true');
      if(success){
        this.track('contact_form_submitted',{lookup_id:saved.lookupId,make:saved.state.make,
          model:saved.state.model,variant:saved.state.variant,year:saved.state.year,
          roof_type:saved.state.roof_type,selected_stage:'contact',
          extra_data_json:JSON.stringify({email_present:true,phone_present:saved.phonePresent,message_present:saved.messagePresent})});
      }
      sessionStorage.removeItem(key);
      sessionStorage.removeItem(`fitmentGlobalActiveForm-${this.root.id}`);
    }
  }
  const init = context => [...(context.matches?.(ROOT)?[context]:[]),...context.querySelectorAll(ROOT)].forEach(root=>{
    if(root.dataset.fitmentGlobalInitialized)return;
    root.dataset.fitmentGlobalInitialized='true';new FitmentGlobal(root).init();
  });
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',()=>init(document),{once:true});
  else init(document);
  document.addEventListener('shopify:section:load',event=>init(event.target));
})();
